"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Menu,
  Home,
  MessageSquare,
  Bell,
  Search,
  LogOut,
  User,
  Shield,
  ChevronDown,
  ChevronRight,
  Info,
  Phone,
  X,
  Settings,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useChat } from "@/lib/chat-context";
import Image from "next/image";

// Content Categories from SideMenu (Dialogues, Capacity Building, Advocacy)
const contentCategories = [
  {
    title: "Dialogues",
    icon: () => (
      <svg
        className="h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 25 25"
        fill="currentColor"
      >
        <g>
          <path d="M3.14 14.9l2.74-2h5.44l-.55 1.44a.5.5 0 00.17.58.47.47 0 00.3.1.49.49 0 00.3-.1l2.72-2h6.08a1.45 1.45 0 001.45-1.45V6.77a1.45 1.45 0 00-1.45-1.45H18.06V3a1.45 1.45 0 00-1.45-1.45H8.12A1.45 1.45 0 006.67 3V5.32H3.45A1.45 1.45 0 002 6.77v4.66a1.46 1.46 0 001 1.36l-.59 1.53a.5.5 0 00.17.58.47.47 0 00.3.1A.49.49 0 003.14 14.9zM18.06 7.7V6.32h2.28a.46.46 0 01.45.45v4.66a.46.46 0 01-.45.45H14.1a.47.47 0 00-.3.1l-1.48 1.1.11-.28s0 0 0 0a1.38 1.38 0 00.34-.18l.06 0a1.14 1.14 0 00.24-.25l0 0a1.35 1.35 0 00.17-.34l0-.09a1.76 1.76 0 00.06-.4V9.15h3.22A1.45 1.45 0 0018.06 7.7zM7.67 3a.46.46 0 01.45-.45h8.49a.46.46 0 01.45.45V7.7a.46.46 0 01-.45.45H13.39V6.77a1.45 1.45 0 00-1.45-1.45H7.67zM3 6.77a.45.45 0 01.44-.45h8.49a.45.45 0 01.45.45v4.66a.45.45 0 01-.45.45H5.71a.47.47 0 00-.3.1l-1.49 1.1.2-.52a.47.47 0 00-.06-.46.48.48 0 00-.41-.22h-.2A.45.45 0 013 11.43zM3.32 17.87A1.88 1.88 0 105.2 16 1.88 1.88 0 003.32 17.87zm2.76 0a.88.88 0 11-1.76 0 .88.88 0 111.76 0zM5.2 20.37A2.63 2.63 0 002.58 23a.51.51 0 00.51.51H7.32a.51.51 0 00.5-.51A2.62 2.62 0 005.2 20.37zM3.66 22.5a1.62 1.62 0 013.09 0zM10.32 17.87A1.88 1.88 0 1012.2 16 1.88 1.88 0 0010.32 17.87zm2.76 0a.88.88 0 01-1.76 0 .88.88 0 011.76 0zM12.2 20.37A2.63 2.63 0 009.58 23a.51.51 0 00.51.51h4.23a.51.51 0 00.5-.51A2.62 2.62 0 0012.2 20.37zM10.66 22.5a1.62 1.62 0 013.09 0zM17.32 17.87A1.88 1.88 0 1019.2 16 1.88 1.88 0 0017.32 17.87zm2.76 0a.88.88 0 01-1.76 0 .88.88 0 011.76 0zM19.2 20.37A2.63 2.63 0 0016.58 23a.51.51 0 00.51.51h4.23a.51.51 0 00.5-.51A2.62 2.62 0 0019.2 20.37zM17.66 22.5a1.62 1.62 0 013.09 0z"></path>
        </g>
      </svg>
    ),
    href: "#dialogues",
    color: "from-blue-500 to-cyan-400",
    subItems: [
      {
        name: "Displaced People Dialogues",
        href: "/articles/hwar-alnazhat",
        query: { id: 5, limit: 0 },
      },
      {
        name: "Localisation Dialogues",
        href: "/articles/hwar-altwtybn",
        query: { id: 6, limit: 0 },
      },
    ],
  },
  {
    title: "Capacity Building",
    icon: () => (
      <svg
        className="h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 128 128"
        fill="currentColor"
      >
        <path d="M64 34a4.92 4.92 0 0 0-4.822 3h9.644A4.92 4.92 0 0 0 64 34Z"></path>
        <path d="M41.52 49.77a40.653 40.653 0 0 1 4.88 8.84 22.016 22.016 0 0 1 1.04 6.59 7.015 7.015 0 0 0 7.04 6.8H62V52h-4a2 2 0 0 1 0-4h12a2 2 0 0 1 0 4h-4v20h7.52a7.02 7.02 0 0 0 7.04-6.8 22.016 22.016 0 0 1 1.04-6.59 40.653 40.653 0 0 1 4.88-8.84 46.485 46.485 0 0 0 5.05-8.97A28.321 28.321 0 0 0 94 29.19C94 13.1 80.54 0 64 0S34 13.1 34 29.19a28.321 28.321 0 0 0 2.47 11.61 46.485 46.485 0 0 0 5.05 8.97zM78 41H50a2 2 0 0 1 0-4h5.08c.55-3.94 4.34-7 8.92-7a9.89 9.89 0 0 1 2.52.33l5.83-8.46a2 2 0 0 1 3.3 2.26l-5.53 8.02a7.71 7.71 0 0 1 2.8 4.85H78a2 2 0 0 1 0 4zM64 8c12.13 0 22 9.51 22 21.19a2 2 0 0 1-4 0C82 19.71 73.92 12 64 12s-18 7.71-18 17.19a2 2 0 0 1-4 0C42 17.51 51.87 8 64 8zm12 70a2 2 0 0 0-2-2H54a2 2 0 1 0 0 4h20a2 2 0 0 0 2-2zm-19 6a2 2 0 1 0 0 4h14a2 2 0 0 0 0-4zM40.74 62.14a1.986 1.986 0 0 0-1.55.03l-19.7 8.75 7.07 8.83a2 2 0 0 1-.31 2.81A1.968 1.968 0 0 1 25 83a1.99 1.99 0 0 1-1.56-.75l-6.64-8.3L.1 125.38c-.34 1.05.18 2.62 1.28 2.62H23a2.004 2.004 0 0 0 1.94-1.52l2.19-8.75 2.95 8.82a2.31 2.31 0 0 0 2.47 1.45H62v-6.946L41.84 63.22a2.008 2.008 0 0 0-1.1-1.08zm87.16 63.24-16.7-51.43-6.64 8.3A2.004 2.004 0 0 1 103 83a1.968 1.968 0 0 1-1.25-.44 2 2 0 0 1-.31-2.81l7.07-8.83-19.7-8.75a1.986 1.986 0 0 0-1.55-.03 2.008 2.008 0 0 0-1.1 1.08L66 121.055V128h31c1.11 0 3.87-10.27 3.87-10.27l2.19 8.75A2.004 2.004 0 0 0 105 128h21.62a2.109 2.109 0 0 0 1.28-2.62z"></path>
      </svg>
    ),
    href: "#capacity",
    color: "from-amber-500 to-orange-400",
    subItems: [
      {
        name: "Training",
        href: "/articles/altdryb",
        query: { id: 7, limit: 0 },
      },
      {
        name: "Peer Learning",
        href: "/articles/talm-alaqran",
        query: { id: 12, limit: 0 },
      },
      {
        name: "Technical Support",
        href: "/articles/dam-fny",
        query: { id: 8, limit: 0 },
      },
    ],
  },
  {
    title: "Advocacy",
    icon: () => (
      <svg
        className="h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2"
      >
        <g>
          <g id="Layer_1">
            <path d="M92.84,74.12l-6.16.97-2.83-5.55c-.2-.39-.62-.6-1.05-.53-.43.07-.76.4-.83.83l-.98,6.16-6.16.98c-.43.07-.76.4-.83.83-.07.43.15.85.53,1.05l5.55,2.83-.97,6.16c-.07.43.15.85.53,1.05.14.07.3.11.45.11.26,0,.51-.1.71-.29l4.41-4.41,5.55,2.83c.39.2.85.12,1.16-.18s.38-.78.18-1.16l-2.83-5.55,4.41-4.41c.31-.31.38-.78.18-1.16-.2-.39-.61-.6-1.05-.53ZM87.37,79.33c-.31.31-.38.78-.18,1.16l1.76,3.46-3.46-1.76c-.39-.2-.85-.12-1.16.18l-2.75,2.75.61-3.84c.07-.43-.15-.85-.53-1.05l-3.46-1.76,3.84-.61c.43-.07.76-.4.83-.83l.61-3.84,1.76,3.46c.2.39.62.61,1.05.53l3.84-.61-2.75,2.75ZM49.29,50.71c-.39-.39-.39-1.02,0-1.41l5.58-5.58c.39-.39,1.02-.39,1.41,0s.39,1.02,0,1.41l-5.58,5.58c-.2.2-.45.29-.71.29s-.51-.1-.71-.29ZM3.66,17.37l6.16-.98,2.83,5.55c.17.34.52.55.89.55.05,0,.1,0,.16-.01.43-.07.76-.4.83-.83l.98-6.16,6.16-.98c.43-.07.76-.4.83-.83.07-.43-.15-.85-.53-1.05l-5.55-2.83.97-6.16c.07-.43-.15-.85-.53-1.05-.39-.2-.85-.12-1.16.18l-4.41,4.41-5.55-2.83c-.39-.2-.85-.12-1.16.18-.31.31-.38.77-.18,1.16l2.83,5.55-4.41,4.41c-.31.31-.38.77-.18,1.16.2.39.62.6,1.05.53ZM9.13,12.17c.31-.31.38-.77.18-1.16l-1.76-3.46,3.46,1.76c.39.2.85.12,1.16-.18l2.75-2.75-.61,3.84c-.07.43.15.85.53,1.05l3.46,1.76-3.83.61c-.43.07-.76.4-.83.83l-.61,3.84-1.76-3.46c-.17-.34-.52-.55-.89-.55-.05,0-.1,0-.16.01l-3.84.61,2.75-2.75ZM32.33,22.44l-1.95,3.83c-.2.39-.12.85.18,1.16.31.31.78.38,1.16.18l3.83-1.95,3.04,3.04c.19.19.45.29.71.29.15,0,.31-.04.45-.11.39-.2.6-.62.53-1.05l-.67-4.25,3.83-1.95c.39-.2.6-.62.53-1.05-.07-.43-.4-.76-.83-.83l-4.25-.67-.67-4.25c-.07-.43-.4-.76-.83-.83-.43-.07-.85.15-1.05.53l-1.95,3.83-4.25-.67c-.43-.07-.85.15-1.05.53-.2.39-.12.85.18,1.16l3.04,3.04ZM34.81,20.47c.43.07.85-.15,1.05-.53l.89-1.74.30,1.92c.07.43.4.76.83.83l1.93.31-1.74.89c-.39.2-.6.62-.53,1.05l.30,1.93-1.38-1.38c-.19-.19-.45-.29-.71-.29-.15,0-.31.04-.45.11l-1.74.89.88-1.74c.2-.39.12-.85-.18-1.16l-1.38-1.38,1.93.31ZM44.35,45.35v-4.65c0-.55.45-1,1-1s1,.45,1,1v4.65c0,.55-.45,1-1,1s-1-.45-1-1ZM97.5,23.26c0-11.45-9.31-20.76-20.76-20.76s-20.76,9.31-20.76,20.76c0,3.1.67,6.07,1.98,8.85v8.94c0,.55.45,1,1,1h8.94c2.78,1.31,5.75,1.98,8.85,1.98,11.45,0,20.76-9.31,20.76-20.76ZM68.12,40.05h-8.17v-8.16c0-.15-.03-.30-.10-.44-1.25-2.56-1.88-5.32-1.88-8.18,0-10.35,8.42-18.76,18.76-18.76s18.76,8.42,18.76,18.76-8.42,18.76-18.76,18.76c-2.87,0-5.62-.63-8.18-1.88-.14-.07-.29-.10-.44-.10ZM60.30,54.65c0,.55-.45,1-1,1h-4.65c-.55,0-1-.45-1-1s.45-1,1-1h4.65c.55,0,1,.45,1,1ZM67.95,20.07c-1.76,0-3.20,1.43-3.20,3.20s1.43,3.20,3.20,3.20,3.20-1.43,3.20-3.20-1.43-3.20-3.20-3.20ZM67.95,24.46c-.66,0-1.20-.54-1.20-1.20s.54-1.20,1.20-1.20,1.20.54,1.20,1.20-.54,1.20-1.20,1.20ZM79.93,23.26c0-1.76-1.43-3.20-3.20-3.20s-3.20,1.43-3.20,3.20,1.43,3.20,3.20,3.20,3.20-1.43,3.20-3.20ZM75.54,23.26c0-.66.54-1.20,1.20-1.20s1.20.54,1.20,1.20-.54,1.20-1.20,1.20-1.20-.54-1.20-1.20ZM48.25,51.75c-10.13-10.13-21.42-14.70-25.71-10.41-1.71,1.71-2.06,4.56-1.07,8.14-1.81,12.17-7.59,23.65-16.28,32.35-1.73,1.73-2.69,4.04-2.69,6.49s.96,4.76,2.69,6.49c1.79,1.79,4.14,2.68,6.49,2.68s4.70-.89,6.49-2.68c.16-.16.32-.30.48-.46,1.37,1.58,3.23,2.64,5.32,3.01.54.10,1.08.14,1.61.14,1.87,0,3.69-.57,5.25-1.66,2.01-1.41,3.35-3.51,3.78-5.93.37-2.11,0-4.24-1.03-6.08,5.34-2.62,11.08-4.44,16.98-5.31,1.30.35,2.52.54,3.62.54,1.86,0,3.40-.52,4.49-1.61,2.16-2.16,2.18-6.10.04-11.09-2.02-4.70-5.73-9.89-10.45-14.62ZM32.64,89.56c-.33,1.89-1.38,3.54-2.95,4.63-1.57,1.10-3.48,1.52-5.37,1.19-1.66-.29-3.14-1.14-4.22-2.41,3.54-3.22,7.48-5.98,11.69-8.23.84,1.46,1.14,3.14.85,4.82ZM16.76,93.39c-2.80,2.80-7.36,2.80-10.16,0-1.36-1.36-2.10-3.16-2.10-5.08s.75-3.72,2.10-5.08c8.19-8.19,13.87-18.77,16.24-30.10,2.07,4.51,5.63,9.41,10.12,13.90,4.65,4.65,9.55,8.13,13.90,10.12-11.33,2.37-21.91,8.04-30.10,16.24ZM30.73,61.64c2.14-1.33,4.99-1.08,6.85.77,1.86,1.86,2.11,4.72.77,6.86-1.32-1.10-2.65-2.31-3.98-3.64-1.30-1.30-2.51-2.64-3.64-3.99ZM57.25,76.04c-1.20,1.19-3.49,1.35-6.43.50-.01,0-.02,0-.03-.01-3.07-.89-6.85-2.88-10.86-5.99,1.97-2.93,1.67-6.95-.92-9.54-2.59-2.59-6.60-2.90-9.53-.93-2.21-2.86-3.97-5.73-5.12-8.43-1.77-4.13-1.91-7.37-.39-8.89.70-.70,1.77-1.05,3.13-1.05,4.49,0,12.11,3.82,19.75,11.46,4.55,4.55,8.11,9.52,10.03,13.99,1.77,4.13,1.91,7.37.39,8.89ZM85.52,26.46c1.76,0,3.20-1.43,3.20-3.20s-1.43-3.20-3.20-3.20-3.20,1.43-3.20,3.20,1.43,3.20,3.20,3.20ZM85.52,22.07c.66,0,1.20.54,1.20,1.20s-.54,1.20-1.20,1.20-1.20-.54-1.20-1.20.54-1.20,1.20-1.20Z" />
          </g>
        </g>
      </svg>
    ),
    href: "#advocacy",
    color: "from-emerald-500 to-teal-400",
    subItems: [
      {
        name: "Reports",
        href: "/articles/reports",
        query: { id: 9, limit: 0 },
      },
      {
        name: "Videos",
        href: "/articles/videos",
        query: { id: 10, limit: 0 },
      },
      {
        name: "News",
        href: "/articles/news",
        query: { id: 11, limit: 0 },
      },
    ],
  },
];

// Simple navigation links
const simpleNavigation = [
  { name: "Home", href: "/", icon: Home },
  { name: "About", href: "/about", icon: Info },
  { name: "Chat", href: "/chat", icon: MessageSquare },
];

// Animation variants
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
      ease: "easeIn",
    },
  },
};

const menuItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (custom: any) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: custom * 0.05,
      duration: 0.2,
    },
  }),
};

const iconVariants = {
  initial: { rotate: 0 },
  open: { rotate: 180 },
};

export default function MainNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { user, logout } = useAuth();
  const { chatRooms } = useChat();

  // Calculate total unread messages
  const totalUnreadMessages = chatRooms.reduce(
    (total, room) => total + (room.unreadCount || 0),
    0,
  );

  const handleLogout = () => {
    logout();
    router.push("/login");
  };

  // Handle dropdown hover
  const handleDropdownHover = (dropdown: any) => {
    setHoveredDropdown(dropdown);
    if (window.innerWidth >= 768) {
      setOpenDropdown(dropdown);
    }
  };

  const handleDropdownLeave = () => {
    setHoveredDropdown(null);
    // Don't close immediately on desktop, let user click to close
    if (window.innerWidth >= 768) {
      setTimeout(() => {
        if (hoveredDropdown === null) {
          setOpenDropdown(null);
        }
      }, 300);
    }
  };

  const handleDropdownClick = (dropdown: any) => {
    if (window.innerWidth < 768) {
      setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-40">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src={"/logo.webp"}
              alt="logo"
              width={120}
              height={120}
              className=" flex items-center justify-center"
            />
              {/* <span className="text-primary-foreground font-bold text-sm"></span> */}
            {/* <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
            >
              Displaced Women Platform
            </motion.span> */}
          </Link>

          {/* Desktop Navigation */}
          {
            <nav className="hidden md:flex items-center gap-1">
              {/* Home Link */}
              <Link href="/">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant={pathname === "/" ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-2"
                  >
                    <Home className="h-4 w-4" />
                    Home
                  </Button>
                </motion.div>
              </Link>

              {/* About Link */}
              <Link href="/about">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant={pathname === "/about" ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-2"
                  >
                    <Info className="h-4 w-4" />
                    About
                  </Button>
                </motion.div>
              </Link>

              {/* Chat Link */}
              {/* <Link href="/chat">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant={pathname === "/chat" ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-2 relative"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Chat
                    {totalUnreadMessages > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                      >
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {totalUnreadMessages}
                        </Badge>
                      </motion.div>
                    )}
                  </Button>
                </motion.div>
              </Link> */}

              {/* Content Categories Dropdown */}
              {contentCategories.map((category) => {
                const Icon = category.icon;
                const isOpen = openDropdown === category.title;
                return (
                  <div
                    key={category.title}
                    className="relative"
                    onMouseEnter={() => handleDropdownHover(category.title)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant={isOpen ? "secondary" : "ghost"}
                        size="sm"
                        className="gap-2"
                        onClick={() => handleDropdownClick(category.title)}
                      >
                        <div
                          className={`p-1 rounded-md bg-gradient-to-br ${category.color}`}
                        >
                          <Icon />
                        </div>
                        <span>{category.title}</span>
                        <motion.div
                          variants={iconVariants}
                          initial="initial"
                          animate={isOpen ? "open" : "initial"}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.div>
                      </Button>
                    </motion.div>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          variants={dropdownVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="absolute left-0 top-full mt-2 w-64 rounded-lg border bg-popover shadow-lg overflow-hidden"
                          onMouseEnter={() =>
                            handleDropdownHover(category.title)
                          }
                          onMouseLeave={handleDropdownLeave}
                        >
                          <div className="p-2">
                            {category.subItems.map((subItem, index) => (
                              <motion.div
                                key={subItem.name}
                                custom={index}
                                variants={menuItemVariants}
                                initial="hidden"
                                animate="visible"
                                className="mb-1 last:mb-0"
                              >
                                <Link
                                  href={{
                                    pathname: subItem.href,
                                    query: subItem.query,
                                  }}
                                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors"
                                >
                                  <div className="h-2 w-2 rounded-full bg-primary" />
                                  <span className="flex-1">{subItem.name}</span>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              {/* Admin Link */}
              {/* {(user.role === "admin" || user.role === "moderator") && (
                <Link href="/admin">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant={
                        pathname.startsWith("/admin") ? "secondary" : "ghost"
                      }
                      size="sm"
                      className="gap-2"
                    >
                      <Shield className="h-4 w-4" />
                      Admin
                    </Button>
                  </motion.div>
                </Link>
              )} */}
            </nav>
          }
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              {/* Search Button with Animation */}
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <Button variant="ghost" size="icon" className="hidden md:flex">
                  <Search className="h-4 w-4" />
                </Button>
              </motion.div>

              {/* Notifications with Animation */}
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-4 w-4" />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                      3
                    </Badge>
                  </motion.div>
                </Button>
              </motion.div>

              {/* User Dropdown with Animation */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="ghost"
                      className="relative h-8 w-8 rounded-full"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage
                          src={user.avatar || "/placeholder.svg"}
                          alt={user.firstName}
                        />
                        <AvatarFallback>
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </motion.div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => router.push("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  {(user.role === "admin" || user.role === "moderator") && (
                    <DropdownMenuItem onClick={() => router.push("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button variant="ghost" size="icon" className="md:hidden">
                      <Menu className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex flex-col gap-4 mt-8"
                  >
                    {/* Home in Mobile */}
                    <Link href="/" onClick={() => setIsOpen(false)}>
                      <motion.div whileHover={{ x: 5 }}>
                        <Button
                          variant={pathname === "/" ? "secondary" : "ghost"}
                          className="w-full justify-start gap-2"
                        >
                          <Home className="h-4 w-4" />
                          Home
                        </Button>
                      </motion.div>
                    </Link>

                    {/* About in Mobile */}
                    <Link href="/about" onClick={() => setIsOpen(false)}>
                      <motion.div whileHover={{ x: 5 }}>
                        <Button
                          variant={
                            pathname === "/about" ? "secondary" : "ghost"
                          }
                          className="w-full justify-start gap-2"
                        >
                          <Info className="h-4 w-4" />
                          About
                        </Button>
                      </motion.div>
                    </Link>

                    {/* Chat in Mobile */}
                    <Link href="/chat" onClick={() => setIsOpen(false)}>
                      <motion.div whileHover={{ x: 5 }}>
                        <Button
                          variant={pathname === "/chat" ? "secondary" : "ghost"}
                          className="w-full justify-start gap-2 relative"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Chat
                          {totalUnreadMessages > 0 && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="ml-auto"
                            >
                              <Badge>{totalUnreadMessages}</Badge>
                            </motion.div>
                          )}
                        </Button>
                      </motion.div>
                    </Link>

                    {/* Content Categories in Mobile */}
                    {contentCategories.map((category) => {
                      const Icon = category.icon;
                      const isOpenCategory = openDropdown === category.title;
                      return (
                        <div key={category.title} className="w-full">
                          <Button
                            variant="ghost"
                            className="w-full justify-between"
                            onClick={() => handleDropdownClick(category.title)}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`p-2 rounded-md bg-gradient-to-br ${category.color}`}
                              >
                                <Icon />
                              </div>
                              <span>{category.title}</span>
                            </div>
                            <motion.div
                              animate={{ rotate: isOpenCategory ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </motion.div>
                          </Button>

                          <AnimatePresence>
                            {isOpenCategory && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden pl-8"
                              >
                                {category.subItems.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    href={{
                                      pathname: subItem.href,
                                      query: subItem.query,
                                    }}
                                    onClick={() => setIsOpen(false)}
                                  >
                                    <motion.div whileHover={{ x: 5 }}>
                                      <Button
                                        variant="ghost"
                                        className="w-full justify-start text-sm h-8"
                                      >
                                        <div className="h-1 w-1 rounded-full bg-primary mr-2" />
                                        {subItem.name}
                                      </Button>
                                    </motion.div>
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}

                    {/* Admin in Mobile */}
                    {(user.role === "admin" || user.role === "moderator") && (
                      <Link href="/admin" onClick={() => setIsOpen(false)}>
                        <motion.div whileHover={{ x: 5 }}>
                          <Button
                            variant={
                              pathname.startsWith("/admin")
                                ? "secondary"
                                : "ghost"
                            }
                            className="w-full justify-start gap-2"
                          >
                            <Shield className="h-4 w-4" />
                            Admin Panel
                          </Button>
                        </motion.div>
                      </Link>
                    )}
                  </motion.div>
                </SheetContent>
              </Sheet>
            </>
          ) : (
            // Non-logged in user actions
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" forceMount>
                  <DropdownMenuItem asChild>
                    <Link href="/login">Sign In</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/register">Sign Up</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link href="/login">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button variant="ghost">Sign In</Button>
                </motion.div>
              </Link>
              <Link href="/register">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button>Sign Up</Button>
                </motion.div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
